import React, { useState } from 'react';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { currentLanguageAtom, changeLanguageAtom } from '../../atoms/shared/uiAtoms';
import { supportedLanguages, supportedLanguageOptions } from '../../lib/i18n';
import { languageManager } from '../../services/languageManager';
import { getUserLanguagePreference } from '../../utils/languageUtils';
import { useLanguageDisplayNames } from '../../hooks/useLanguageDisplayNames';

interface LanguageQuickSwitcherProps {
  className?: string;
  variant?: 'minimal' | 'compact' | 'full';
}

/**
 * 快速语言切换组件
 * 专为探索界面等场景设计的简洁语言切换器
 */
export const LanguageQuickSwitcher: React.FC<LanguageQuickSwitcherProps> = ({
  className = '',
  variant = 'minimal'
}) => {
  const { t } = useTranslation(['v3/common']);
  const [currentLanguage] = useAtom(currentLanguageAtom);
  const [, changeLanguage] = useAtom(changeLanguageAtom);
  const [isOpen, setIsOpen] = useState(false);
  const currentUserPreference = getUserLanguagePreference() || 'system';
  const { getLanguageDisplayName } = useLanguageDisplayNames();

  // 选择语言
  const handleSelectLanguage = async (langCode: string) => {
    try {
      await languageManager.setUserLanguagePreference(langCode);
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to set language preference:', error);
      // 回退到原来的方法
      changeLanguage(langCode as keyof typeof supportedLanguages);
      setIsOpen(false);
    }
  };

  // 获取当前语言显示名称
  const getCurrentLanguageName = () => {
    return getLanguageDisplayName(currentUserPreference);
  };



  if (variant === 'minimal') {
    return (
      <div className={`relative flex justify-center ${className}`}>
        <motion.button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-3 px-6 py-3 rounded-full bg-white/90 dark:bg-gray-800/90 backdrop-blur-md border border-gray-200/50 dark:border-gray-600/50 text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-700 shadow-lg hover:shadow-xl transition-all duration-200"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <span className="text-lg font-bold">{t('v3/common:language.icon', '文A')}</span>
          <span className="text-sm font-medium">{t('v3/common:language.switchLanguage', '切换语言')}</span>
          <span className="text-xs text-gray-500 dark:text-gray-400">({getCurrentLanguageName()})</span>
        </motion.button>

        <AnimatePresence>
          {isOpen && (
            <>
              {/* 背景遮罩 */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-40"
                onClick={() => setIsOpen(false)}
              />

              {/* 语言选择菜单 */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: -10 }}
                className="absolute top-full mt-2 left-0 min-w-[120px] bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl border border-white/20 dark:border-gray-700/30 shadow-lg z-50"
              >
                {Object.entries(supportedLanguages).map(([code, name]) => (
                  <motion.button
                    key={code}
                    onClick={() => handleSelectLanguage(code)}
                    className={`w-full px-4 py-3 text-left text-sm transition-colors first:rounded-t-xl last:rounded-b-xl ${
                      code === currentLanguage
                        ? 'bg-blue-500/20 text-blue-600 dark:text-blue-400 font-medium'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100/50 dark:hover:bg-gray-700/50'
                    }`}
                    whileHover={{ backgroundColor: code === currentLanguage ? undefined : 'rgba(0,0,0,0.05)' }}
                  >
                    <div className="flex items-center justify-between">
                      <span>{name}</span>
                      {code === currentLanguage && (
                        <svg className="w-4 h-4 text-blue-500" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </div>
                  </motion.button>
                ))}
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div className={`relative ${className}`}>
        <motion.button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 7.5V9M15 11.5V9.5L21 9V11L15 11.5ZM11 11.5L5 11L5 9L11 9.5V11.5ZM7 18C7.5 18 8 17.5 8 17S7.5 16 7 16 6 16.5 6 17 6.5 18 7 18ZM17 18C17.5 18 18 17.5 18 17S17.5 16 17 16 16 16.5 16 17 16.5 18 17 18ZM12 13.5C12.8 13.5 13.5 14.2 13.5 15S12.8 16.5 12 16.5 10.5 15.8 10.5 15 11.2 13.5 12 13.5Z" fill="currentColor"/>
          </svg>
          <span className="text-sm">{getCurrentLanguageName()}</span>
          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </motion.button>

        <AnimatePresence>
          {isOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-40"
                onClick={() => setIsOpen(false)}
              />

              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: -10 }}
                className="absolute top-full mt-2 left-0 min-w-full bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-lg z-50"
              >
                {Object.entries(supportedLanguages).map(([code, name]) => (
                  <button
                    key={code}
                    onClick={() => handleSelectLanguage(code)}
                    className={`w-full px-4 py-3 text-left text-sm transition-colors first:rounded-t-lg last:rounded-b-lg ${
                      code === currentLanguage
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>{name}</span>
                      {code === currentLanguage && (
                        <svg className="w-4 h-4 text-blue-500" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </div>
                  </button>
                ))}
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // full variant - 默认样式
  return (
    <div className={`relative ${className}`}>
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 px-6 py-3 rounded-xl bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm border border-gray-200 dark:border-gray-700"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 7.5V9M15 11.5V9.5L21 9V11L15 11.5ZM11 11.5L5 11L5 9L11 9.5V11.5ZM7 18C7.5 18 8 17.5 8 17S7.5 16 7 16 6 16.5 6 17 6.5 18 7 18ZM17 18C17.5 18 18 17.5 18 17S17.5 16 17 16 16 16.5 16 17 16.5 18 17 18ZM12 13.5C12.8 13.5 13.5 14.2 13.5 15S12.8 16.5 12 16.5 10.5 15.8 10.5 15 11.2 13.5 12 13.5Z" fill="currentColor"/>
        </svg>
        <span>{getCurrentLanguageName()}</span>
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40"
              onClick={() => setIsOpen(false)}
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: -10 }}
              className="absolute top-full mt-2 left-0 min-w-full bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg z-50"
            >
              {Object.entries(supportedLanguageOptions).map(([code]) => (
                <button
                  key={code}
                  onClick={() => handleSelectLanguage(code)}
                  className={`w-full px-6 py-4 text-left transition-colors first:rounded-t-xl last:rounded-b-xl ${
                    code === currentUserPreference
                      ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{getLanguageDisplayName(code)}</span>
                    {code === currentUserPreference && (
                      <svg className="w-5 h-5 text-blue-500" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                </button>
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LanguageQuickSwitcher;
